// EventBus — class-based topics, parallel emit via Promise.all, group-scoped offGroup.
// Pattern from RESEARCH.md § "Pattern 2: EventBus class-based topics" (D-33, D-34).

// biome-ignore lint/suspicious/noExplicitAny: constructor signature must accept any args
type Constructor<T> = new (...args: any[]) => T
type Listener<E> = (e: E) => void | Promise<void>

interface ListenerEntry<E> {
  fn: Listener<E>
  group?: string
}

export class EventBus {
  #listeners = new Map<Constructor<unknown>, Set<ListenerEntry<unknown>>>()

  on<E>(EventCtor: Constructor<E>, fn: Listener<E>, group?: string): () => void {
    let set = this.#listeners.get(EventCtor as Constructor<unknown>)
    if (!set) {
      set = new Set()
      this.#listeners.set(EventCtor as Constructor<unknown>, set)
    }
    const entry: ListenerEntry<unknown> =
      group !== undefined ? { fn: fn as Listener<unknown>, group } : { fn: fn as Listener<unknown> }
    set.add(entry)
    return () => {
      set?.delete(entry)
    }
  }

  // D-34: parallel emit
  async emit<E>(event: E): Promise<void> {
    const ctor = (event as object).constructor as Constructor<unknown>
    const set = this.#listeners.get(ctor)
    if (!set) return
    await Promise.all(Array.from(set, (e) => e.fn(event)))
  }

  async emitSerial<E>(event: E): Promise<void> {
    const ctor = (event as object).constructor as Constructor<unknown>
    const set = this.#listeners.get(ctor)
    if (!set) return
    for (const e of set) await e.fn(event)
  }

  offGroup(group: string): void {
    for (const set of this.#listeners.values()) {
      for (const entry of set) if (entry.group === group) set.delete(entry)
    }
  }
}
